import { createSignal, For } from "solid-js";

import { formatFileSize } from "../util/format-file-size";

function getItemsFromEvent(ev: DragEvent) {
    const output: DataTransferItem[] = [];
    if (!ev.dataTransfer) {
        return output;
    }
    const items = Array.from(ev.dataTransfer.items);
    for (let item of items) {
        if (item.kind !== "file") {
            continue;
        }
        output.push(item);
    }
    return output;
}

function getFilesFromEvent(ev: DragEvent) {
    const output: File[] = [];
    if (!ev.dataTransfer) {
        return output;
    }
    const items = Array.from(ev.dataTransfer.items);
    for (let item of items) {
        if (item.kind !== "file") {
            continue;
        }
        const file = item.getAsFile();
        if (!file) {
            continue;
        }
        output.push(file);
    }
    return output;
}

function getFilesFromFileInput(el: HTMLInputElement): File[] {
    const output: File[] = [];
    if (!el.files) {
        return output;
    }
    for (let i = 0; i < el.files.length; i++) {
        const file = el.files.item(i);
        if (!file) {
            continue;
        }
        output.push(file)
    }
    return output;
}

type FileUploaderProps = {
    validate: (files: File[]) => void;
    cancel: () => void;
}

export function FileUploadSelector(props: FileUploaderProps) {
    const [ files, setFiles ] = createSignal<File[]>([]);

    let filesRef!: HTMLInputElement;
    let listRef!: HTMLUListElement;

    const addFiles = (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
        filesRef.click();
    };

    const cancel = (ev: Event) => {
        clearFiles(ev);
        props.cancel();
    }

    const clearFiles = (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
        setFiles([]);
    };

    const removeFile = (index: number) => (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
        setFiles((f) => f.filter((_, i) => i !== index))
    };

    const fileInputChange = (_: Event) => {
        const files = getFilesFromFileInput(filesRef);
        if (files.length === 0) {
            return;
        }
        setFiles((f) => [ ...f, ...files ]);
        filesRef.files = null;
        filesRef.value = "";
    };

    const formDragExit = () => {
        listRef?.classList.remove("over");
    };

    const formDragOver = (ev: DragEvent) => {
        const files = getItemsFromEvent(ev);
        if (files.length === 0) {
            return;
        }
        ev.preventDefault();
        ev.dataTransfer!.dropEffect = "copy";
        listRef?.classList.add("over");
    }

    const formDrop = (ev: DragEvent) => {
        ev.preventDefault();
        ev.stopPropagation();
        const files = getFilesFromEvent(ev);
        if (files.length === 0) {
            return;
        }
        setFiles((f) => [ ...f, ...files ]);
        listRef?.classList.remove("over");
    };

    const formSubmit = (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
        props.validate(files());
        setFiles([]);
    };

    return <form
        onSubmit={formSubmit}
        onDrop={formDrop}
        onDragOver={formDragOver}
        onDragExit={formDragExit}
        onDragLeave={formDragExit}
        class="file-uploader">
        <ul ref={listRef}>
            <For each={files()}>
                {(file, index) => <li>
                    <span class="name">{file.name}</span>
                    <span class="size">{formatFileSize(file.size)}</span>
                    <a href="#" class="icon" onClick={removeFile(index())}>delete</a>
                </li>}
            </For>
        </ul>
        <input
            ref={filesRef}
            type="file"
            style="display: none"
            multiple
            onChange={fileInputChange}/>
        <div>
            <button onClick={addFiles} >Ajouter un fichier</button>
            <button onClick={clearFiles}>Tout effacer</button>
            <input type="submit" class="accent" value="Valider"/>
            <button onClick={cancel}>Annuler</button>
        </div>
    </form>
}